import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api';
import {
  CreatePendingPaymentRequest,
  ErrorMessage,
  InitiatePendingPaymentResponse,
  PayPendingPaymentRequest,
} from '../../../generated-api';
import { parseError } from '../../utils/error-handler';
import { useLocation } from '../../context/LocationContext';

export const useMutateCreatePendingPayment = () => {
  const apiClient = useApiClient();

  const postInitiatePayment = (requestParameters: CreatePendingPaymentRequest) =>
    apiClient.PendingPaymentControllerApi.createPendingPayment(requestParameters).catch(parseError);

  return useMutation<InitiatePendingPaymentResponse, ErrorMessage, CreatePendingPaymentRequest>({
    mutationKey: ['create-pending-payment'],
    mutationFn: postInitiatePayment,
    throwOnError: true,
  });
};

export const usePendingPaymentPolling = (paymentMethodId: string) => {
  const apiClient = useApiClient();
  const { location } = useLocation();
  const { data, refetch, isSuccess } = useQuery({
    queryKey: ['paymentMethodId', paymentMethodId],
    queryFn: async () =>
      apiClient.PendingPaymentControllerApi.poll({ locationId: location.locationId, paymentMethodId }),
    retry: true,
    retryDelay: 1000,
    // refetchOnWindowFocus: false,
  });
  return { data, isSuccess, refetch };
};

export const usePayPendingPayment = () => {
  const apiClient = useApiClient();
  return useMutation({
    mutationKey: ['pay-pending-payment'],
    mutationFn: async (requestParameters: PayPendingPaymentRequest) =>
      apiClient.PendingPaymentControllerApi.payPendingPayment(requestParameters).catch(parseError),
    throwOnError: true,
  });
};

export const usePendingPaymentStatePolling = (
  pendingPaymentId: number | undefined,
  paymentMethodId: string | undefined
) => {
  const apiClient = useApiClient();
  const { location } = useLocation();

  const { data, refetch } = useQuery({
    queryKey: ['pendingPaymentState', pendingPaymentId],
    queryFn: async () => {
      if (!pendingPaymentId || !paymentMethodId) {
        return null;
      }

      return apiClient.PendingPaymentControllerApi.getPendingPaymentState({
        locationId: location.locationId,
        paymentMethodId,
        pendingPaymentId,
      });
    },
  });

  return { data, refetch };
};
